import { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import css from "@styled-system/css";
import moment from "moment";

import { DatePickerContext } from "./DatePickerContext";
import { DatePickerCalendarProps, Day, WeekDays } from "./datePicker.type";
import { dayNames, monthNames } from "./enums";
import { getMonthDetails, getYearsRange } from "./helpers";

import Box from "../Box/Box";
import Typography from "../Typography/Typography";

import DatePickerCalendarItem from "./DatePickerCalendarItem";

const DatePickerCalendarHead = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  ${css({
    marginBottom: 3,
  })}
`;

const DatePickerCalendarHeadLabel = styled(Typography)`
  flex: 14.2857%;
  text-align: center;
  ${css({
    fontSize: 1,
    color: "primary-navy-blue",
  })}
`;

const DatePickerCalendarBody = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const DatePickerCalendar = ({
  disabledDays,
  minDate,
  maxDate,
  dateFormat,
  rangePicker,
  onChange,
  onRangeChange,
}: DatePickerCalendarProps) => {
  const {
    selectedYearsRange,
    selectedYear,
    selectedMonth,
    selectedDay,
    selectedRangeStart,
    selectedRangeEnd,
    setSelectedDay,
    setSelectedMonth,
    setSelectedRangeStart,
    setSelectedRangeEnd,
    setSelectedYear,
    resetSelectedRange,
    calendarLayout,
  } = useContext(DatePickerContext);

  const [monthDetails, setMonthDetails] = useState<Day[]>([]);

  const onDateClick = (day: Day) => {
    if (rangePicker) {
      if (selectedRangeStart) {
        if (selectedRangeStart > day.timestamp) {
          resetSelectedRange();
          onRangeChange("", "");
        } else {
          setSelectedRangeEnd(day.timestamp);
          onRangeChange(
            moment(selectedRangeStart).format(dateFormat),
            moment(day.timestamp).format(dateFormat)
          );
        }
      } else {
        setSelectedRangeStart(day.timestamp);
      }
    } else {
      setSelectedDay(day.timestamp);
      onChange(moment(day.timestamp).format(dateFormat));
    }
  };

  useEffect(() => {
    setMonthDetails(getMonthDetails(selectedYear, selectedMonth));
  }, [selectedMonth, selectedYear]);

  const monthDays = monthDetails.map((day: Day, index: number) => {
    const currentYear = moment().year();
    const currentMonth = moment().month();
    const currentDay = moment().date();
    const currentDayTimestamp = moment([
      currentYear,
      currentMonth,
      currentDay,
    ]).valueOf();

    const isHidden = day.month !== 0;
    const isSelected = rangePicker
      ? day.timestamp === selectedRangeStart ||
        day.timestamp === selectedRangeEnd
      : day.timestamp === selectedDay;
    const isCurrent = day.timestamp === currentDayTimestamp;

    const isInRange =
      selectedRangeStart !== null &&
      selectedRangeEnd !== null &&
      selectedRangeStart !== selectedRangeEnd &&
      day.timestamp >= selectedRangeStart &&
      day.timestamp <= selectedRangeEnd;

    const isRangeStart =
      selectedRangeStart !== null &&
      selectedRangeEnd !== null &&
      selectedRangeStart !== selectedRangeEnd &&
      day.timestamp === selectedRangeStart;

    const isRangeEnd =
      selectedRangeStart !== null &&
      selectedRangeEnd !== null &&
      selectedRangeStart !== selectedRangeEnd &&
      day.timestamp === selectedRangeEnd;

    const isMinDate =
      typeof minDate !== "undefined" &&
      moment(minDate).valueOf() > day.timestamp;

    const isMaxDate =
      typeof maxDate !== "undefined" &&
      moment(maxDate).valueOf() < day.timestamp;
    const isInDisabledDays =
      disabledDays && disabledDays.includes(day.day as WeekDays);
    const isDisabled = isInDisabledDays || isMinDate || isMaxDate;

    return (
      <DatePickerCalendarItem
        key={index}
        onClick={() => onDateClick(day)}
        isSelected={isSelected}
        isCurrent={isCurrent}
        isHidden={isHidden}
        isInRange={isInRange}
        isRangeStart={isRangeStart}
        isRangeEnd={isRangeEnd}
        disabled={isDisabled}
      >
        {day.date}
      </DatePickerCalendarItem>
    );
  });

  const yearMonths = Object.keys(monthNames)
    .filter((key: string | number) => isNaN(Number(key)))
    .map((month: string, index: number) => {
      const currentYear = moment().year();
      const currentMonth = moment().month();
      const isSelected = selectedMonth === index;
      const isCurrent = index === currentMonth && selectedYear === currentYear;
      const isMinDate =
        typeof minDate !== "undefined" &&
        moment(minDate).month() > index &&
        moment(minDate).year() >= selectedYear;
      const isMaxDate =
        typeof maxDate !== "undefined" &&
        moment(maxDate).month() < index &&
        moment(maxDate).year() <= selectedYear;
      const isDisabled = isMinDate || isMaxDate;

      return (
        <DatePickerCalendarItem
          key={index}
          isSelected={isSelected}
          isCurrent={isCurrent}
          onClick={() => setSelectedMonth(index)}
          disabled={isDisabled}
        >
          {month.substring(0, 3)}
        </DatePickerCalendarItem>
      );
    });

  const { startYear, endYear } = selectedYearsRange;
  const yearsRange = getYearsRange(startYear, endYear).map(
    (year: number, index: number) => {
      const currentYear = moment().year();
      const isSelectedYear = selectedYear === year;
      const isCurrent = year === currentYear;
      const isDisabled =
        (typeof minDate !== "undefined" && moment(minDate).year() > year) ||
        (typeof maxDate !== "undefined" && moment(maxDate).year() < year);
      return (
        <DatePickerCalendarItem
          key={index}
          isSelected={isSelectedYear}
          isCurrent={isCurrent}
          onClick={() => setSelectedYear(year)}
          disabled={isDisabled}
        >
          {year}
        </DatePickerCalendarItem>
      );
    }
  );

  return (
    <Box aria-label="datepicker calendar">
      {calendarLayout && calendarLayout === "days" && (
        <DatePickerCalendarHead>
          {Object.keys(dayNames)
            .filter((key: string | number) => isNaN(Number(key)))
            .map((day: string, index: number) => (
              <DatePickerCalendarHeadLabel key={index} htmlTag="span">
                {day.substring(0, 2)}
              </DatePickerCalendarHeadLabel>
            ))}
        </DatePickerCalendarHead>
      )}
      <DatePickerCalendarBody aria-label="calendar body">
        {
          {
            days: monthDays,
            months: yearMonths,
            years: yearsRange,
          }[calendarLayout]
        }
      </DatePickerCalendarBody>
    </Box>
  );
};

export default DatePickerCalendar;
